import { useMemo } from 'react';

import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { contactsAPI } from '@/api/contacts';
import type { Contact, ContactsResponse } from '@/shared/types/contacts';

interface UseContactsParams {
  limit?: number;
  sort?: string;
  q?: string;
}

const buildSearchParams = (params: UseContactsParams, cursor?: string) => {
  const searchParams = new URLSearchParams();
  if (params.limit) {
    searchParams.set('limit', String(params.limit));
  }
  if (params.sort) {
    searchParams.set('sort', params.sort);
  }
  if (params.q) {
    searchParams.set('q', params.q);
  }
  if (cursor) {
    searchParams.set('next_cursor', cursor);
  }
  return searchParams;
};

export const useContacts = (params?: UseContactsParams) => {
  const queryClient = useQueryClient();
  const queryKey = ['contacts/list', params?.limit, params?.sort, params?.q];

  const { data, status, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery<ContactsResponse>({
      queryKey,
      queryFn: ({ pageParam }) =>
        contactsAPI.listContacts(buildSearchParams(params ?? {}, pageParam as string | undefined)),
      getNextPageParam: (lastPage) =>
        lastPage.pagination?.has_more ? lastPage.pagination.next_cursor : undefined,
      initialPageParam: undefined as string | undefined,
      enabled: !!params,
      placeholderData: keepPreviousData,
    });

  const invalidateContactsList = () => {
    queryClient.invalidateQueries({ queryKey: ['contacts/list'] });
  };

  const createMutation = useMutation<Contact, Error, Partial<Contact>>({
    mutationFn: (payload) => contactsAPI.createContact(payload),
    onSuccess: invalidateContactsList,
  });

  const deleteMutation = useMutation<void, Error, { contactId: string; force: boolean }>({
    mutationFn: ({ contactId, force }) => contactsAPI.deleteContact(contactId, force),
    onSuccess: () => {
      invalidateContactsList();
      queryClient.invalidateQueries({ queryKey: ['debts/list'] });
    },
  });

  const pages = useMemo(() => data?.pages ?? [], [data?.pages]);
  const total = useMemo(() => pages[0]?.pagination?.total, [pages]);

  return {
    pages,
    total,
    status,
    error,
    fetchNextPage,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    createContact: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    deleteContact: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
};
